// src/features/landing/presentation/pages/RbBridgeConsultPage.jsx
// RB Bridge 상담 신청 페이지 — /rb-bridge/consult/:slug (buyer | seller | partner)
// 이름·연락처·이메일 + 개인정보 수집·이용 동의 → POST /api/mna/clients (recruit-api, mna_client).
// 서비스 이용약관은 회원가입과 같은 라이징브로커즈 이용약관(terms SERVICE 활성 버전)을 모달로 보여주고 동의를 받는다.
// 이용약관 동의는 제출 조건일 뿐 API로 보내지 않는다(mna_client에 컬럼 없음).
// 실패 시 서버 message를 그대로, 네트워크 오류면 카카오톡·전화 안내를 함께 보여준다.
// rbBridgeConsultRepository.isAvailable=false로 되돌리면 API를 호출하지 않고 "온라인 접수 준비 중" 안내를 보여준다
// (백엔드 장애·미배포 시 비상용).
import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Footer } from "../components/Footer";
import { rbBridgeConsultRepository } from "../../infrastructure/rbBridgeConsultApiRepository";
import { termsRepository } from "../../infrastructure/termsApiRepository";
import {
    RB_BRIDGE_CONSULT_TYPES,
    RB_BRIDGE_KAKAO_URL,
    RB_BRIDGE_TEL,
    findConsultType,
    formatPhone,
    readUtm,
    validateConsultForm,
} from "../../domain/rbBridgeConsult";
import "../styles/landing.css";

const EMPTY_FORM = { name: "", phone: "", email: "", privacyAgreed: false, serviceAgreed: false };

const NEXT_STEPS = [
    { title: "담당자 확인", body: "남겨 주신 정보는 RB Bridge 담당자만 확인합니다." },
    { title: "1차 상담 연락", body: "담당자가 남겨 주신 연락처로 직접 연락드립니다." },
    { title: "방향 정리", body: "시장 상황과 절차를 안내드리고 다음 단계를 함께 정합니다. 결정은 그 뒤에 하셔도 됩니다." },
];

const PROMISES = [
    "조직명·회사명은 묻지 않습니다.",
    "상담만으로 등록되지 않습니다.",
    "동의 없이 어떤 정보도 공개하지 않습니다.",
];

export function RbBridgeConsultPage() {
    const { slug } = useParams();
    const consult = findConsultType(slug);

    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("form"); // form | submitting | done | unavailable | error
    const [errorMessage, setErrorMessage] = useState("");
    const [networkError, setNetworkError] = useState(false);
    const [serviceTerms, setServiceTerms] = useState(null);
    const [termsStatus, setTermsStatus] = useState("idle"); // idle | loading | done | error
    const [showTerms, setShowTerms] = useState(false);
    const cardRef = useRef(null);

    // 상단 탭으로 상담 유형을 바꾸면 입력 상태 초기화
    useEffect(() => {
        setForm(EMPTY_FORM);
        setErrors({});
        setStatus("form");
        setErrorMessage("");
        setNetworkError(false);
    }, [slug]);

    const openTerms = () => {
        setShowTerms(true);
        if (termsStatus === "loading" || termsStatus === "done") return;
        setTermsStatus("loading");
        termsRepository.getTermsByType("SERVICE")
            .then((data) => {
                setServiceTerms(data);
                setTermsStatus("done");
            })
            .catch(() => setTermsStatus("error"));
    };

    const agreeTerms = () => {
        setForm((prev) => ({ ...prev, serviceAgreed: true }));
        if (errors.serviceAgreed) setErrors((prev) => ({ ...prev, serviceAgreed: undefined }));
        setShowTerms(false);
    };

    // 모달이 열려 있는 동안 Esc로 닫기
    useEffect(() => {
        if (!showTerms) return undefined;
        const onKey = (e) => { if (e.key === "Escape") setShowTerms(false); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [showTerms]);

    const showResult = (next) => {
        setStatus(next);
        requestAnimationFrame(() => cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const next = type === "checkbox" ? checked : name === "phone" ? formatPhone(value) : value;
        setForm((prev) => ({ ...prev, [name]: next }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const found = validateConsultForm(form);
        setErrors(found);
        if (Object.keys(found).length > 0) {
            const first = ["name", "phone", "email", "privacyAgreed", "serviceAgreed"].find((k) => found[k]);
            document.querySelector(`.rbcp-form [name="${first}"]`)?.focus();
            return;
        }
        if (!rbBridgeConsultRepository.isAvailable) {
            showResult("unavailable");
            return;
        }
        setStatus("submitting");
        setErrorMessage("");
        setNetworkError(false);
        try {
            await rbBridgeConsultRepository.createClient({
                clientType: consult.type,
                name: form.name.trim(),
                phone: form.phone.replace(/-/g, ""), // 계약: 숫자만 (입력 칸 표시는 하이픈 포맷 유지)
                email: form.email.trim(),
                privacyAgreed: true,
                ...readUtm(),
            });
            showResult("done");
        } catch (err) {
            setErrorMessage(err.message);
            setNetworkError(!!err.isNetworkError);
            setStatus("error");
        }
    };

    const search = typeof window !== "undefined" ? window.location.search : "";

    return (
        <>
            {!consult ? (
                <Navigate to="/rb-bridge" replace />
            ) : (
                <main className="rbcp">
                    <header className="rbcp-header">
                        <div className="rbcp-container">
                            <nav className="rbcp-crumb" aria-label="현재 위치">
                                <Link to="/rb-bridge">RB Bridge</Link>
                                <span aria-hidden="true">/</span>
                                <span>상담 신청</span>
                            </nav>
                            <p className={`rbcp-en rbcp-tone--${consult.tone}`}>{consult.en}</p>
                            <h1 className="rbcp-title">{consult.label} 신청</h1>
                            <p className="rbcp-lead">{consult.lead}</p>

                            <div className="rbcp-tabs" role="tablist" aria-label="상담 유형">
                                {RB_BRIDGE_CONSULT_TYPES.map((c) => (
                                    <Link
                                        key={c.slug}
                                        to={`/rb-bridge/consult/${c.slug}${search}`}
                                        replace
                                        role="tab"
                                        aria-selected={c.slug === consult.slug}
                                        className={`rbcp-tab ${c.slug === consult.slug ? "rbcp-tab--on" : ""}`}
                                    >
                                        {c.label}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </header>

                    <section className="rbcp-body">
                        <div className="rbcp-container rbcp-grid">
                            <div className="rbcp-card" ref={cardRef}>
                                {status === "done" && (
                                    <div className="rbcp-result">
                                        <span className="rbcp-result-mark rbcp-result-mark--ok" aria-hidden="true">✓</span>
                                        <h2 className="rbcp-result-title">상담 신청이 접수되었습니다.</h2>
                                        <p className="rbcp-result-body">담당자가 확인 후 남겨 주신 연락처로 연락드립니다. 상담만으로 등록되지 않습니다.</p>
                                        <Link to="/rb-bridge" className="rbcp-btn rbcp-btn--ghost">RB Bridge로 돌아가기</Link>
                                    </div>
                                )}

                                {status === "unavailable" && (
                                    <div className="rbcp-result">
                                        <span className="rbcp-result-mark" aria-hidden="true">i</span>
                                        <h2 className="rbcp-result-title">온라인 접수는 준비 중입니다.</h2>
                                        <p className="rbcp-result-body">
                                            지금은 카카오톡 채널이나 전화로 상담을 도와드리고 있습니다.
                                            &lsquo;{consult.label}&rsquo;이라고 남겨 주시면 담당자가 바로 안내드립니다.
                                        </p>
                                        <div className="rbcp-result-actions">
                                            <a href={RB_BRIDGE_KAKAO_URL} target="_blank" rel="noopener noreferrer" className="rbcp-btn rbcp-btn--primary">카카오톡으로 상담하기</a>
                                            <a href={`tel:${RB_BRIDGE_TEL}`} className="rbcp-btn rbcp-btn--ghost">전화 {RB_BRIDGE_TEL}</a>
                                        </div>
                                        <button type="button" className="rbcp-link" onClick={() => setStatus("form")}>입력 화면으로 돌아가기</button>
                                    </div>
                                )}

                                {(status === "form" || status === "submitting" || status === "error") && (
                                    <form className="rbcp-form" onSubmit={handleSubmit} noValidate>
                                        <h2 className="rbcp-card-title">연락받으실 정보</h2>
                                        <p className="rbcp-card-sub">조직명·회사명은 적지 않으셔도 됩니다. <em>*</em> 표시는 필수 항목입니다.</p>

                                        <label className="rbcp-field">
                                            <span className="rbcp-label">이름 <em>*</em></span>
                                            <input name="name" value={form.name} onChange={handleChange} maxLength={50}
                                                   placeholder="이름을 입력해 주세요" autoComplete="name"
                                                   aria-invalid={!!errors.name} />
                                            {errors.name && <span className="rbcp-error">{errors.name}</span>}
                                        </label>

                                        <label className="rbcp-field">
                                            <span className="rbcp-label">연락처 <em>*</em></span>
                                            <input name="phone" value={form.phone} onChange={handleChange} inputMode="numeric"
                                                   placeholder="연락처를 입력해 주세요" autoComplete="tel" aria-invalid={!!errors.phone} />
                                            {errors.phone && <span className="rbcp-error">{errors.phone}</span>}
                                        </label>

                                        <label className="rbcp-field">
                                            <span className="rbcp-label">이메일 <em>*</em></span>
                                            <input name="email" type="email" value={form.email} onChange={handleChange} maxLength={100}
                                                   placeholder="이메일을 입력해 주세요" autoComplete="email" aria-invalid={!!errors.email} />
                                            {errors.email && <span className="rbcp-error">{errors.email}</span>}
                                        </label>

                                        <div className="rbcp-privacy">
                                            <p className="rbcp-privacy-title">개인정보 수집·이용 안내</p>
                                            <dl className="rbcp-privacy-rows">
                                                <div><dt>수집 항목</dt><dd>이름, 연락처, 이메일</dd></div>
                                                <div><dt>이용 목적</dt><dd>RB Bridge 상담 연락 및 매수·매도·제휴 진행 관리</dd></div>
                                                <div><dt>보유 기간</dt><dd>삭제를 요청하실 때까지 보관합니다</dd></div>
                                            </dl>
                                            <p className="rbcp-privacy-note">동의하지 않으실 수 있으며, 이 경우 온라인 상담 신청이 제한됩니다.</p>
                                            <label className="rbcp-check">
                                                <input type="checkbox" name="privacyAgreed" checked={form.privacyAgreed} onChange={handleChange} />
                                                <span>개인정보 수집·이용에 동의합니다. (필수)</span>
                                            </label>
                                            {errors.privacyAgreed && <span className="rbcp-error">{errors.privacyAgreed}</span>}
                                            <div className="rbcp-check-row">
                                                <label className="rbcp-check">
                                                    <input type="checkbox" name="serviceAgreed" checked={form.serviceAgreed} onChange={handleChange} />
                                                    <span>라이징브로커즈 서비스 이용약관에 동의합니다. (필수)</span>
                                                </label>
                                                <button type="button" className="rbcp-terms-link" onClick={openTerms}>내용보기</button>
                                            </div>
                                            {errors.serviceAgreed && <span className="rbcp-error">{errors.serviceAgreed}</span>}
                                        </div>

                                        {status === "error" && (
                                            <div className="rbcp-error rbcp-error--box" role="alert">
                                                <p>{errorMessage}</p>
                                                {networkError && (
                                                    <p className="rbcp-error-contact">
                                                        계속 안 되면 <a href={RB_BRIDGE_KAKAO_URL} target="_blank" rel="noopener noreferrer">카카오톡 채널</a>이나
                                                        전화(<a href={`tel:${RB_BRIDGE_TEL}`}>{RB_BRIDGE_TEL}</a>)로 &lsquo;{consult.label}&rsquo;이라고 남겨 주세요.
                                                    </p>
                                                )}
                                            </div>
                                        )}

                                        <button type="submit" className="rbcp-btn rbcp-btn--primary rbcp-submit" disabled={status === "submitting"}>
                                            {status === "submitting" ? "접수 중..." : "상담 신청하기"}
                                        </button>
                                        <p className="rbcp-foot">상담 내용과 연락처는 담당자 외에 공유되지 않습니다.</p>
                                    </form>
                                )}
                            </div>

                            <aside className="rbcp-aside">
                                <div className="rbcp-aside-block">
                                    <h2 className="rbcp-aside-title">신청 후 이렇게 진행됩니다</h2>
                                    <ol className="rbcp-steps">
                                        {NEXT_STEPS.map((s, i) => (
                                            <li key={s.title}>
                                                <span className={`rbcp-step-no rbcp-step-no--${i % 2 === 0 ? "orange" : "brown"}`}>{i + 1}</span>
                                                <div>
                                                    <p className="rbcp-step-title">{s.title}</p>
                                                    <p className="rbcp-step-body">{s.body}</p>
                                                </div>
                                            </li>
                                        ))}
                                    </ol>
                                </div>

                                <div className="rbcp-aside-block">
                                    <h2 className="rbcp-aside-title">약속드리는 것</h2>
                                    <ul className="rbcp-promises">
                                        {PROMISES.map((p) => (
                                            <li key={p}><span aria-hidden="true">✓</span>{p}</li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="rbcp-aside-block rbcp-aside-block--contact">
                                    <h2 className="rbcp-aside-title">바로 연락하기</h2>
                                    <a href={`tel:${RB_BRIDGE_TEL}`} className="rbcp-contact">
                                        <span>대표번호</span>
                                        <strong>{RB_BRIDGE_TEL}</strong>
                                    </a>
                                    <a href={RB_BRIDGE_KAKAO_URL} target="_blank" rel="noopener noreferrer" className="rbcp-contact">
                                        <span>카카오톡 채널</span>
                                        <strong>라이징브로커즈</strong>
                                    </a>
                                </div>
                            </aside>
                        </div>
                    </section>
                </main>
            )}
            <Footer />

            {showTerms && (
                <div className="rbcp-modal-backdrop" onClick={() => setShowTerms(false)}>
                    <div className="rbcp-modal" role="dialog" aria-modal="true" aria-labelledby="rbcp-modal-title" onClick={(e) => e.stopPropagation()}>
                        <div className="rbcp-modal-hd">
                            <h3 id="rbcp-modal-title">{serviceTerms?.title || "서비스 이용약관"}</h3>
                            <button type="button" className="rbcp-modal-close" aria-label="닫기" onClick={() => setShowTerms(false)}>✕</button>
                        </div>
                        <div className="rbcp-modal-body">
                            {termsStatus === "done" && serviceTerms?.content
                                ? serviceTerms.content.split("\n").map((line, i) => <p key={i}>{line || <br />}</p>)
                                : termsStatus === "error"
                                    ? <p>이용약관을 불러오지 못했습니다. <Link to="/terms" target="_blank" rel="noopener noreferrer">이용약관 페이지</Link>에서 확인해 주세요.</p>
                                    : <p>불러오는 중...</p>}
                        </div>
                        <div className="rbcp-modal-ft">
                            <button type="button" className="rbcp-btn rbcp-btn--primary" onClick={agreeTerms}>동의하고 닫기</button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                /* 이용약관 모달은 .rbcp 밖(Footer 뒤)에 렌더되므로 색상 변수를 함께 선언 */
                .rbcp, .rbcp-modal-backdrop {
                    --ink: #1c1814; --text: #4a433d; --muted: #8a8079; --line: #e5ddd7;
                    --cream: #f7f4f1; --orange: #f16102; --orange-dark: #d95502; --brown: #c2926b;
                    color: var(--ink); text-align: left; word-break: keep-all;
                }
                .rbcp *, .rbcp *::before, .rbcp *::after { box-sizing: border-box; }
                .rbcp-container { max-width: 1120px; margin: 0 auto; padding: 0 40px; }

                .rbcp-header { padding: 48px 0 0; background: #fff; border-bottom: 1px solid var(--line); }
                .rbcp-crumb { display: flex; gap: 8px; font-size: 13px; color: var(--muted); margin-bottom: 32px; }
                .rbcp-crumb a { color: var(--muted); text-decoration: none; }
                .rbcp-crumb a:hover { color: var(--orange); }
                .rbcp-en { margin: 0 0 10px; font-size: 13px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; }
                .rbcp-tone--orange { color: var(--orange); }
                .rbcp-tone--brown { color: #b07d55; }
                .rbcp-tone--ink { color: var(--ink); }
                .rbcp-title { margin: 0; font-size: clamp(30px, 3.6vw, 42px); font-weight: 800; letter-spacing: -0.02em; }
                .rbcp-lead { margin: 16px 0 0; max-width: 640px; font-size: 16px; line-height: 1.75; color: var(--text); }
                .rbcp-tabs { display: flex; gap: 28px; margin-top: 40px; }
                .rbcp-tab {
                    padding: 0 0 16px; font-size: 15px; font-weight: 700; color: var(--muted);
                    text-decoration: none; border-bottom: 3px solid transparent; margin-bottom: -1px;
                }
                .rbcp-tab:hover { color: var(--ink); }
                .rbcp-tab--on { color: var(--ink); border-bottom-color: var(--orange); }

                .rbcp-body { padding: 56px 0 96px; background: var(--cream); }
                .rbcp-grid { display: grid; grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr); gap: 32px; align-items: start; }

                .rbcp-card {
                    background: #fff; border: 1px solid var(--line); border-radius: 16px; padding: 40px;
                    box-shadow: 0 2px 12px rgba(28, 24, 20, 0.04); scroll-margin-top: 100px;
                }
                .rbcp-form { display: flex; flex-direction: column; gap: 20px; }
                .rbcp-card-title { margin: 0; font-size: 20px; font-weight: 800; }
                .rbcp-card-sub { margin: -12px 0 4px; font-size: 14px; color: var(--muted); }
                .rbcp-card-sub em, .rbcp-label em { font-style: normal; color: var(--orange); }
                .rbcp-field { display: flex; flex-direction: column; gap: 8px; }
                .rbcp-label { font-size: 14px; font-weight: 700; }
                .rbcp-field input {
                    width: 100%; padding: 14px 16px; font-size: 16px; font-family: inherit; color: var(--ink);
                    border: 1px solid var(--line); border-radius: 8px; background: #fff;
                }
                .rbcp-field input:focus { outline: none; border-color: var(--orange); box-shadow: 0 0 0 3px rgba(241, 97, 2, 0.12); }
                .rbcp-field input[aria-invalid="true"] { border-color: #d93025; }
                .rbcp-error { font-size: 13px; color: #d93025; }
                .rbcp-error--box { margin: 0; padding: 12px 14px; background: #fdecea; border-radius: 8px; }
                .rbcp-error--box p { margin: 0; }
                .rbcp-error--box .rbcp-error-contact { margin-top: 6px; color: var(--text); }
                .rbcp-error-contact a { color: inherit; text-decoration: underline; }

                .rbcp-privacy { padding: 18px 20px; background: var(--cream); border-radius: 10px; }
                .rbcp-privacy-title { margin: 0 0 10px; font-size: 13px; font-weight: 800; }
                .rbcp-privacy-rows { margin: 0; }
                .rbcp-privacy-rows > div { display: grid; grid-template-columns: 72px minmax(0, 1fr); gap: 8px; padding: 4px 0; font-size: 13px; line-height: 1.6; }
                .rbcp-privacy-rows dt { color: var(--muted); font-weight: 600; }
                .rbcp-privacy-rows dd { margin: 0; color: var(--text); }
                .rbcp-privacy-note { margin: 8px 0 14px; font-size: 12px; color: var(--muted); }
                .rbcp-check { display: flex; align-items: center; gap: 10px; font-size: 14px; font-weight: 700; cursor: pointer; }
                .rbcp-check input { width: 18px; height: 18px; flex-shrink: 0; accent-color: var(--orange); }
                .rbcp-check-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--line); }
                .rbcp-terms-link { flex-shrink: 0; padding: 0; border: 0; background: none; font: inherit; font-size: 13px; color: var(--muted); text-decoration: underline; cursor: pointer; }
                .rbcp-terms-link:hover { color: var(--orange); }

                .rbcp-modal-backdrop { position: fixed; inset: 0; z-index: 1100; display: flex; align-items: center; justify-content: center; padding: 20px; background: rgba(28, 24, 20, 0.5); }
                .rbcp-modal { display: flex; flex-direction: column; width: 100%; max-width: 560px; max-height: 80vh; background: #fff; border-radius: 14px; color: #1c1814; text-align: left; }
                .rbcp-modal-hd { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 18px 24px; border-bottom: 1px solid #e5ddd7; }
                .rbcp-modal-hd h3 { margin: 0; font-size: 17px; font-weight: 800; }
                .rbcp-modal-close { padding: 4px; border: 0; background: none; font-size: 16px; color: #8a8079; cursor: pointer; }
                .rbcp-modal-body { flex: 1; overflow-y: auto; padding: 18px 24px; font-size: 14px; line-height: 1.8; color: #4a433d; word-break: keep-all; }
                .rbcp-modal-body p { margin: 0 0 4px; }
                .rbcp-modal-body a { color: #f16102; }
                .rbcp-modal-ft { display: flex; justify-content: flex-end; padding: 14px 24px; border-top: 1px solid #e5ddd7; }
                .rbcp-modal-ft .rbcp-btn { padding: 11px 24px; font-size: 15px; }

                .rbcp-btn {
                    display: inline-flex; align-items: center; justify-content: center; padding: 15px 24px;
                    border-radius: 8px; font-size: 16px; font-weight: 700; font-family: inherit; text-decoration: none; cursor: pointer;
                }
                .rbcp-btn--primary { background: var(--orange); color: #fff; border: 1px solid var(--orange); }
                .rbcp-btn--primary:hover { background: var(--orange-dark); border-color: var(--orange-dark); }
                .rbcp-btn--primary:disabled { background: #f5a46f; border-color: #f5a46f; cursor: default; }
                .rbcp-btn--ghost { background: #fff; color: var(--ink); border: 1px solid var(--line); }
                .rbcp-btn--ghost:hover { border-color: var(--brown); color: #a97a55; }
                .rbcp-submit { width: 100%; margin-top: 4px; }
                .rbcp-foot { margin: -8px 0 0; text-align: center; font-size: 13px; color: var(--muted); }

                .rbcp-result { display: flex; flex-direction: column; align-items: flex-start; gap: 14px; padding: 8px 0; }
                .rbcp-result-mark {
                    display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px; border-radius: 50%;
                    background: var(--cream); color: var(--brown); font-weight: 800; font-size: 20px; font-family: Georgia, serif;
                }
                .rbcp-result-mark--ok { background: var(--orange); color: #fff; font-family: inherit; }
                .rbcp-result-title { margin: 0; font-size: 22px; font-weight: 800; }
                .rbcp-result-body { margin: 0; font-size: 15px; line-height: 1.75; color: var(--text); }
                .rbcp-result-actions { display: flex; gap: 10px; flex-wrap: wrap; margin-top: 6px; }
                .rbcp-link { margin-top: 4px; padding: 0; border: 0; background: none; font: inherit; font-size: 14px; color: var(--muted); text-decoration: underline; cursor: pointer; }

                .rbcp-aside { display: flex; flex-direction: column; gap: 16px; }
                .rbcp-aside-block { padding: 28px; background: #fff; border: 1px solid var(--line); border-radius: 16px; }
                .rbcp-aside-title { margin: 0 0 18px; font-size: 16px; font-weight: 800; }
                .rbcp-steps { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 18px; }
                .rbcp-steps li { display: grid; grid-template-columns: 32px minmax(0, 1fr); gap: 14px; }
                .rbcp-step-no {
                    display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 50%;
                    color: #fff; font-size: 14px; font-weight: 800;
                }
                .rbcp-step-no--orange { background: var(--orange); }
                .rbcp-step-no--brown { background: var(--brown); }
                .rbcp-step-title { margin: 4px 0 4px; font-size: 15px; font-weight: 800; }
                .rbcp-step-body { margin: 0; font-size: 14px; line-height: 1.7; color: var(--text); }
                .rbcp-promises { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 12px; }
                .rbcp-promises li { display: flex; gap: 10px; font-size: 15px; font-weight: 600; }
                .rbcp-promises span { color: var(--orange); font-weight: 800; }
                .rbcp-aside-block--contact { background: var(--ink); border-color: var(--ink); color: #fff; }
                .rbcp-aside-block--contact .rbcp-aside-title { color: #fff; }
                .rbcp-contact {
                    display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 14px 0;
                    border-top: 1px solid rgba(255, 255, 255, 0.14); color: #fff; text-decoration: none; font-size: 14px;
                }
                .rbcp-contact span { color: rgba(255, 255, 255, 0.65); }
                .rbcp-contact strong { font-size: 16px; }
                .rbcp-contact:hover strong { color: #ff8a3d; }

                @media (max-width: 960px) {
                    .rbcp-grid { grid-template-columns: minmax(0, 1fr); }
                }
                @media (max-width: 768px) {
                    .rbcp-container { padding: 0 20px; }
                    .rbcp-header { padding-top: 28px; }
                    .rbcp-crumb { margin-bottom: 20px; }
                    .rbcp-lead { font-size: 15px; }
                    .rbcp-tabs { gap: 20px; margin-top: 28px; overflow-x: auto; }
                    .rbcp-tab { white-space: nowrap; font-size: 14px; }
                    .rbcp-body { padding: 24px 0 64px; }
                    .rbcp-card { padding: 24px 20px; }
                    .rbcp-aside-block { padding: 22px 20px; }
                    .rbcp-result-actions { flex-direction: column; width: 100%; }
                    .rbcp-result-actions .rbcp-btn { width: 100%; }
                }
            `}</style>
        </>
    );
}
